import React, { useState, FC, useEffect, Fragment, useContext } from 'react';
import { IconButton, TableRow } from '@mui/material';
import { DataTableCell, DataTableValue } from 'components/shared/data-table/data-table.componet';
import { MaterialBatch, MaterialPriceList } from 'models/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DataTableTextField from 'components/shared/data-table/data-table-text-field.component';
import DataTableCodeAutocomplete from 'components/shared/data-table/data-table-code-autocomplete.component';
import DataTableAutocomplete, {
    DataTableAutocompleteOption,
} from 'components/shared/data-table/data-table-autocomplete.component';
import { formatCurrency } from 'utils/format';
import { useFormik } from 'formik';
import { emptyMaterialInput, isMaterialRowEmpty } from './form-helper';
import { isNumeric } from 'utils/validations';
import { MaterialGridInput } from 'models/order-input/shared';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import isEqual from 'lodash/isEqual';
import DiscountButton from '../shared/discount-button.component';
import { CurrencyContext } from 'context/currency';
import MaterialBatchManager from 'components/lab-settings/material-settings/materials/material-batch-manager.component.tsx/material-batch-manager.component';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useMutation } from '@apollo/client';
import { materialBatchManagementTemplate } from 'apollo/templates/material';
import { getTime } from 'date-fns';
import { findPriceListMaterial, requiresBatchSelection, toNumber } from '../actions-and-materials-helper';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';

const vatOptions = [{ key: '0' }, { key: '6' }, { key: '12' }, { key: '25' }];

type MaterialsDataGridRowProps = {
    material: MaterialGridInput;
    priceList: MaterialPriceList;
    onChange?: (material: MaterialGridInput) => void;
    isPlaceholderRow?: boolean;
    warranty?: boolean;
    dataTest?: string;
};
const MaterialsDataGridRow: FC<MaterialsDataGridRowProps> = ({
    material,
    priceList,
    onChange,
    isPlaceholderRow = false,
    warranty,
    dataTest,
}) => {
    const { t: translate } = useTranslation(['orders', 'actions-materials', 'lab-settings']);
    const { currency } = useContext(CurrencyContext);
    const [selectedCell, setSelectedCell] = useState<string>();
    const [selectedAutocompleteItem, setSelectedAutocompleteItem] = useState<DataTableAutocompleteOption | null>(null);
    const [totalExclVat, setTotalExclVat] = useState<string>();
    const [totalIncVat, setTotalIncVat] = useState<string>();
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [hasChangedOnce, setHasChangedOnce] = useState(false);
    const [batchInventoryManagement, setBatchInventoryManagement] = useState(false);
    const [batches, setBatches] = useState<MaterialBatch[]>([]);
    const [batchManagerOpen, setBatchManagerOpen] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();
    const currentUser = currentUserVar();
    const canManageBatches = permissions.isTechnicianAdmin(currentUser?.role);

    const [materialBatchManagement] = useMutation(materialBatchManagementTemplate, {
        onCompleted({ materialBatchManagement }) {
            const openBatches = materialBatchManagement.batches.filter((batch: MaterialBatch) => !batch.closed);
            setBatches(materialBatchManagement.batches || []);
            if (onChange) {
                const batchNumber = openBatches.length > 0 ? openBatches[0].batchNumber : '';
                // Also bypasses formik - carry the verdict so a row that was blocked on a
                // missing batch is released once one has been opened here.
                onChange({
                    ...material,
                    batchNumber,
                    isValid: Boolean(batchNumber) || !requiresBatchSelection(priceList?.materials, material._id),
                });
            }
            setBatchManagerOpen(false);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const validate = (formData: MaterialGridInput) => {
        const errors: Record<string, string> = {};
        if (isMaterialRowEmpty(formData)) {
            return errors;
        }
        // A new line needs an article from the catalogue; a stored line keeps its own code
        // and description even when they are not (or no longer) in the catalogue (DD-2506).
        if (formData.description && !formData._id && !formData.rowId) {
            errors._id = 'ERROR';
        }
        if (!formData.description) {
            errors.description = 'ERROR';
        }
        if (!isNumeric(formData.quantity)) {
            errors.quantity = 'ERROR';
        }
        if (!isNumeric(formData.unitPrice)) {
            errors.unitPrice = 'ERROR';
        }
        // Read from the price list rather than the batchInventoryManagement state - that
        // state is filled by an effect which runs after the first validation pass, so it
        // would give a stale verdict on a row that was just added.
        if (requiresBatchSelection(priceList?.materials, formData._id) && !formData.batchNumber) {
            errors.batchNumber = 'ERROR';
        }
        return errors;
    };

    // Validity emitted to the grid is computed from the values being emitted, never from
    // formik.isValid: that flag lags one render behind and can be stale-false when a cell
    // blurs in the same keystroke that picked a batch (the blur validates the old, empty
    // batch). A later blur would otherwise re-emit that stale verdict and hold the step back.
    const isRowValid = (formData: MaterialGridInput) => Object.keys(validate(formData)).length === 0;

    const formik = useFormik<MaterialGridInput>({
        initialValues: emptyMaterialInput(),
        validate,
        onSubmit: async (materialInput) => {
            if (onChange) {
                onChange(materialInput);
            }
        },
    });

    useEffect(() => {
        const values = {
            _id: material._id,
            // The stored row this grid row edits — carried through like actionId below.
            rowId: material.rowId,
            materialCode: material.materialCode,
            description: material.description,
            batchNumber: material.batchNumber,
            quantity: material.quantity?.toString() || '',
            basePrice: material.basePrice || 0,
            unitPrice: material.unitPrice || '',
            vatPercent: material.vatPercent || '',
            priceWithVat: material.priceWithVat || 0,
            priceWithoutVat: material.priceWithoutVat || 0,
            isValid: material.isValid || true,
            isActionMaterial: material.isActionMaterial || false,
            // The grid replaces a row with whatever this form emits, so the link back to
            // the action has to be carried through here or it is lost on the first edit.
            actionId: material.actionId,
            originalPrice: material.originalPrice || 0,
            originalVat: material.originalVat || 0,
        };
        if (!isEqual(formik.values, values)) {
            formik.setValues(values, false);
            setHasChangedOnce(!isMaterialRowEmpty(values));
            if (material._id) {
                setSelectedAutocompleteItem({
                    id: material._id || '',
                    key: material.materialCode || '',
                    value: material.description || '',
                });
            } else {
                setSelectedAutocompleteItem(null);
            }
        }
    }, [material, warranty]);

    // Keyed on the row's material, not just the price list: rows added programmatically
    // (macro materials, actionMaterials) arrive long after the price list has loaded, and
    // they never pass through handleMaterialSelectChanged which sets this state directly.
    useEffect(() => {
        const priceListMaterial = findPriceListMaterial(priceList?.materials, material?._id);
        if (priceListMaterial) {
            setBatchInventoryManagement(!!priceListMaterial.batchInventoryManagement);
            setBatches(priceListMaterial.batches || []);
        }
    }, [priceList, material?._id]);

    useEffect(() => {
        // Handle total price caluclations
        try {
            setTotalExclVat(formatCurrency(0, currency));
            setTotalIncVat(formatCurrency(0, currency));
            formik.setFieldValue('priceWithoutVat', 0, false);
            formik.setFieldValue('priceWithVat', 0, false);

            if (warranty) {
                formik.setFieldValue('unitPrice', 0, false);
                return;
            }

            let total = 0;
            if (formik.values.unitPrice && formik.values.quantity) {
                const priceNum = parseFloat(formik.values.unitPrice.replace(',', '.'));
                const quantityNum = parseFloat(formik.values.quantity.replace(',', '.'));
                total = priceNum * quantityNum;
                if (isNaN(total)) {
                    total = 0;
                }
                setTotalExclVat(formatCurrency(total, currency));
                setTotalIncVat(formatCurrency(total, currency));
                formik.setFieldValue('priceWithoutVat', total, false);
                formik.setFieldValue('priceWithVat', total, false);
            }

            if (formik.values.vatPercent && formik.values.vatPercent !== '0') {
                const vatNum = parseFloat(formik.values.vatPercent.replace(',', '.')) / 100 + 1;
                setTotalIncVat(formatCurrency(total * vatNum, currency));
                formik.setFieldValue('priceWithVat', total * vatNum, false);
            }
        } catch (error) {
            setTotalExclVat('0');
            setTotalIncVat('0');
        }
    }, [formik.values.quantity, formik.values.unitPrice, formik.values.vatPercent, warranty]);

    const isCellSelected = (colId: string) => {
        return selectedCell === colId;
    };

    const handleMaterialSelectChanged = (value: DataTableAutocompleteOption | null) => {
        setSelectedAutocompleteItem(value);
        if (priceList?.materials) {
            const material = priceList.materials.find((material) => material._id === value?.id) as any;
            if (material) {
                const newValues = {
                    ...formik.values,
                    _id: material.material,
                    batchNumber: material.batches?.find((batch: any) => !batch.closed)?.batchNumber || '',
                    materialCode: material.materialCode || '',
                    // Picking an article applies it in full: code, description and prices.
                    description: material.description,
                    quantity: formik.values.quantity || '1',
                    basePrice: material.basePrice || 0,
                    unitPrice: material.price?.toString() || '',
                    vatPercent: material.vat?.toString() || '0',
                    priceWithoutVat: material.price || 0,
                    priceWithVat:
                        material.vat && material.vat > 0
                            ? material.price * (1 + material.vat / 100)
                            : material.price || 0,
                    originalPrice: material.originalPrice ? material.originalPrice : material.basePrice ?? 0,
                    originalVat: material.vat || 0,
                } as MaterialGridInput;
                setHasChangedOnce(true);
                formik.setValues(newValues, true);
                setBatchInventoryManagement(!!material.batchInventoryManagement);
                setBatches(material.batches || []);
                if (onChange) {
                    onChange({ ...newValues, isValid: true });
                }
            }
        }
    };

    const handleDeleteConfirm = () => {
        setDeleteConfirmOpen(false);
        if (onChange) {
            onChange(emptyMaterialInput());
        }
    };

    const priceListAsOptions = () => {
        if (priceList?.materials) {
            return priceList.materials
                .map((material) => {
                    return {
                        id: material._id || '',
                        key: material.materialCode || '',
                        value: material.description,
                    };
                })
                .sort((a, b) => a.key.localeCompare(b.key));
        }
        return [];
    };

    const handleDiscountChange = (discountPercent: string) => {
        const { unitPrice, quantity, vatPercent } = formik.values;
        const priceNum = toNumber(unitPrice);
        const quantityNum = toNumber(quantity);
        const vatMultiplier = toNumber(vatPercent) / 100 + 1;

        let newUnitPrice: string;

        if (discountPercent.trim() === '') {
            // No discount → reset to original price
            newUnitPrice = priceNum.toFixed(2);
        } else {
            const percent = parseFloat(discountPercent);
            if (!isNaN(percent) && percent >= 0 && percent <= 100) {
                const discountedPrice = priceNum * (1 - percent / 100);
                newUnitPrice = discountedPrice.toFixed(2);
            } else {
                // Invalid discount → keep old price
                newUnitPrice = priceNum.toFixed(2);
            }
        }

        const priceWithoutVat = +(parseFloat(newUnitPrice) * quantityNum).toFixed(2);
        const priceWithVat = +(priceWithoutVat * vatMultiplier).toFixed(2);

        const updatedValues = {
            ...formik.values,
            unitPrice: newUnitPrice,
            priceWithoutVat,
            priceWithVat,
        };
        formik.setValues(updatedValues);
        if (onChange) {
            onChange({
                ...updatedValues,
                isValid: isRowValid(updatedValues),
            });
        }
    };

    const handleFieldChange = (field: keyof MaterialGridInput) => (e: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange(e);
        if (!hasChangedOnce) {
            setHasChangedOnce(true);
            // If this is the first change set default values and trigger onChange
            formik.values.quantity = '1';
            formik.values.unitPrice = '0';
            formik.values.vatPercent = '0';
            if (onChange) {
                const changedValues = { ...formik.values, [field]: e.target.value };
                onChange({ ...changedValues, isValid: isRowValid(changedValues) });
            }
        }
    };

    const createTooltipText = (material: MaterialGridInput) => {
        if (!isPlaceholderRow && material.basePrice !== 0 && parseFloat(material.unitPrice) !== material.basePrice) {
            return `${translate('orders:REGULAR_PRICE')}: ${formatCurrency(material.basePrice, currency)}`;
        }
        return undefined;
    };

    const batchesVars = (batches: MaterialBatch[]) => {
        const batchesVars: any = [];
        batches.forEach((batch) => {
            batchesVars.push({
                batchNumber: batch.batchNumber,
                quantity: batch.fullBatchQuantity || 0,
                fullBatchQuantity: batch.fullBatchQuantity || 0,
                createdOn: typeof batch.createdOn === 'number' ? batch.createdOn : getTime(batch.createdOn),
                closed: batch.closed || false,
                closedDate:
                    typeof batch.closedDate === 'number'
                        ? batch.closedDate
                        : batch.closedDate
                          ? getTime(batch.closedDate)
                          : null,
            });
        });
        return batchesVars;
    };

    const handleSaveBatchChanges = async (batches: MaterialBatch[]) => {
        await materialBatchManagement({
            variables: { materialCode: material.materialCode, batches: batchesVars(batches) },
        });
    };

    return (
        <Fragment>
            <TableRow
                data-test={dataTest || 'materials-data-grid-row'}
                sx={
                    isPlaceholderRow
                        ? {
                              opacity: 0.1,
                          }
                        : undefined
                }
            >
                <DataTableCell>
                    <DataTableCodeAutocomplete
                        code={formik.values.materialCode || ''}
                        disabled={formik.values.isActionMaterial}
                        selected={isCellSelected('material')}
                        onFocus={() => setSelectedCell('material')}
                        onBlur={() => setSelectedCell(undefined)}
                        error={Boolean(formik.errors._id)}
                        options={priceListAsOptions()}
                        onSelectValue={handleMaterialSelectChanged}
                    />
                </DataTableCell>
                <DataTableCell>
                    {formik.values.isActionMaterial ? (
                        <DataTableValue>{formik.values.description}</DataTableValue>
                    ) : (
                        <DataTableTextField
                            autoComplete="off"
                            variant="standard"
                            onFocus={() => setSelectedCell('description')}
                            fullWidth
                            InputProps={{ disableUnderline: true }}
                            selected={isCellSelected('description')}
                            error={formik.touched.description && Boolean(formik.errors.description)}
                            {...formik.getFieldProps('description')}
                            onChange={handleFieldChange('description')}
                            onBlur={(e) => {
                                formik.getFieldProps('description').onBlur(e);
                                setSelectedCell(undefined);
                                if (hasChangedOnce && onChange) {
                                    onChange({ ...formik.values, isValid: isRowValid(formik.values) });
                                }
                            }}
                        />
                    )}
                </DataTableCell>
                <DataTableCell>
                    {batchInventoryManagement ? (
                        <Fragment>
                            {batches?.filter((batch) => !batch.closed).length === 0 ? (
                                canManageBatches ? (
                                    <DataTableAutocomplete
                                        options={[
                                            {
                                                key: `${translate('actions-materials:MANAGE_BATCHES')}`,
                                            },
                                        ]}
                                        selectedValue={{ key: '' }}
                                        onSelectValue={() => setBatchManagerOpen(true)}
                                    />
                                ) : null
                            ) : (
                                <DataTableAutocomplete
                                    selected={isCellSelected('batchNumber')}
                                    // Not gated on `touched` - a macro row is created
                                    // programmatically, so the lab has to be able to see
                                    // which cell is holding the step back.
                                    error={Boolean(formik.errors.batchNumber)}
                                    // A batch-tracked material cannot go back to having no
                                    // batch once one is chosen - only switch to another.
                                    disableClearable
                                    onFocus={() => setSelectedCell('batchNumber')}
                                    onBlur={(e) => {
                                        formik.getFieldProps('batchNumber').onBlur(e);
                                        setSelectedCell(undefined);
                                    }}
                                    options={[
                                        ...batches
                                            .filter((batch: MaterialBatch) => !batch.closed)
                                            .map((batch) => ({
                                                key: batch.batchNumber,
                                            })),
                                        ...(canManageBatches
                                            ? [
                                                  {
                                                      id: '__manage__',
                                                      key: `${translate('actions-materials:MANAGE_BATCHES')}`,
                                                  },
                                              ]
                                            : []),
                                    ]}
                                    selectedValue={
                                        material.batchNumber
                                            ? {
                                                  key: material.batchNumber || '',
                                              }
                                            : null
                                    }
                                    onSelectValue={(selected) => {
                                        if (selected?.id === '__manage__') {
                                            if (canManageBatches) {
                                                setBatchManagerOpen(true);
                                            }
                                            return;
                                        }
                                        const batchNumber = selected?.key || '';
                                        // This path bypasses formik, so it has to carry the
                                        // new verdict itself or a row that was invalid for a
                                        // missing batch would stay invalid after picking one.
                                        onChange?.({
                                            ...material,
                                            batchNumber,
                                            isValid:
                                                Boolean(batchNumber) ||
                                                !requiresBatchSelection(priceList?.materials, material._id),
                                        });
                                    }}
                                />
                            )}
                        </Fragment>
                    ) : (
                        <DataTableTextField
                            autoComplete="off"
                            variant="standard"
                            onFocus={() => setSelectedCell('batchNumber')}
                            fullWidth
                            InputProps={{ disableUnderline: true }}
                            selected={isCellSelected('batchNumber')}
                            error={formik.touched.batchNumber && Boolean(formik.errors.batchNumber)}
                            {...formik.getFieldProps('batchNumber')}
                            onChange={handleFieldChange('batchNumber')}
                            onBlur={(e) => {
                                formik.getFieldProps('batchNumber').onBlur(e);
                                setSelectedCell(undefined);
                                if (hasChangedOnce && onChange) {
                                    onChange({ ...formik.values, isValid: isRowValid(formik.values) });
                                }
                            }}
                        />
                    )}
                </DataTableCell>
                <DataTableCell>
                    <DataTableTextField
                        autoComplete="off"
                        variant="standard"
                        onFocus={() => setSelectedCell('quantity')}
                        fullWidth
                        InputProps={{ disableUnderline: true }}
                        selected={isCellSelected('quantity')}
                        alignRight
                        error={formik.touched.quantity && Boolean(formik.errors.quantity)}
                        {...formik.getFieldProps('quantity')}
                        onChange={handleFieldChange('quantity')}
                        onBlur={(e) => {
                            formik.getFieldProps('quantity').onBlur(e);
                            setSelectedCell(undefined);
                            if (hasChangedOnce && onChange) {
                                onChange({ ...formik.values, isValid: isRowValid(formik.values) });
                            }
                        }}
                    />
                </DataTableCell>
                <DataTableCell>
                    {warranty || formik.values.isActionMaterial ? (
                        <DataTableValue alignRight>{isPlaceholderRow ? '' : formik.values.unitPrice}</DataTableValue>
                    ) : (
                        <DataTableTextField
                            autoComplete="off"
                            variant="standard"
                            tooltipText={createTooltipText(formik.values)}
                            onFocus={() => setSelectedCell('unitPrice')}
                            fullWidth
                            InputProps={{ disableUnderline: true }}
                            selected={isCellSelected('unitPrice')}
                            alignRight
                            error={formik.touched.unitPrice && Boolean(formik.errors.unitPrice)}
                            {...formik.getFieldProps('unitPrice')}
                            onChange={handleFieldChange('unitPrice')}
                            onBlur={(e) => {
                                formik.getFieldProps('unitPrice').onBlur(e);
                                setSelectedCell(undefined);
                                if (hasChangedOnce && onChange) {
                                    onChange({ ...formik.values, isValid: isRowValid(formik.values) });
                                }
                            }}
                        />
                    )}
                </DataTableCell>
                <DataTableCell readonly>
                    {!isPlaceholderRow && <DataTableValue alignRight>{totalExclVat}</DataTableValue>}
                </DataTableCell>
                <DataTableCell>
                    {!isPlaceholderRow ? (
                        <DataTableAutocomplete
                            selected={isCellSelected('vatPercent')}
                            disabled={formik.values.isActionMaterial}
                            onFocus={() => setSelectedCell('vatPercent')}
                            onBlur={(e) => {
                                formik.getFieldProps('vatPercent').onBlur(e);
                                setSelectedCell(undefined);
                                if (hasChangedOnce && onChange) {
                                    onChange({ ...formik.values, isValid: isRowValid(formik.values) });
                                }
                            }}
                            options={vatOptions}
                            onSelectValue={(value) => {
                                const newValues = { ...formik.values, vatPercent: value?.key || '0' };
                                formik.setFieldValue('vatPercent', value?.key || '0');
                                if (onChange) {
                                    onChange({ ...newValues, isValid: isRowValid(newValues) });
                                }
                            }}
                            selectedValue={{ key: formik.values.vatPercent || '0' }}
                            width="100px"
                        />
                    ) : undefined}
                </DataTableCell>
                <DataTableCell readonly>
                    {!isPlaceholderRow && <DataTableValue alignRight>{totalIncVat}</DataTableValue>}
                </DataTableCell>
                <DataTableCell>
                    {!isPlaceholderRow && (
                        <Fragment>
                            <DiscountButton onChange={handleDiscountChange} disabled={formik.values.isActionMaterial} />
                            <IconButton
                                onClick={() => setDeleteConfirmOpen(true)}
                                size="small"
                                data-test="materials-data-grid-row.delete-material"
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                            <ConfirmDialog
                                open={deleteConfirmOpen}
                                handleClose={() => setDeleteConfirmOpen(false)}
                                dialogContent={translate('actions-materials:CONFIRM_DELETE_MATERIAL')}
                                handleConfirm={handleDeleteConfirm}
                            />
                        </Fragment>
                    )}
                </DataTableCell>
            </TableRow>
            {/* Dialog for batch management */}
            <DHDialog
                fullWidth
                maxWidth="lg"
                open={batchManagerOpen}
                title={translate('lab-settings:BATCH_INVENTORY')}
                onClose={() => setBatchManagerOpen(false)}
            >
                <MaterialBatchManager
                    materialToEdit={{
                        ...formik.values,
                        materialCode: material.materialCode || '',
                        description: material.description || '',
                        batches: batches,
                        batchInventoryManagement: batchInventoryManagement,
                    }}
                    onBatches={(batches: MaterialBatch[]) => setBatches(batches || [])}
                    openBatchManagerInDialog
                    onClose={() => setBatchManagerOpen(false)}
                    onSaveBatchChanges={handleSaveBatchChanges}
                />
            </DHDialog>
        </Fragment>
    );
};

export default MaterialsDataGridRow;
